import { Contact, CRMStats } from '@/types/crm';

// Configuração - substitua com suas credenciais
const CONFIG = {
  SHEET_ID: 'SEU_SHEET_ID_AQUI',
  API_KEY: 'SUA_API_KEY_AQUI',
  SHEET_NAME: 'Lista de Contatos - La Sorelle',
  POLLING_INTERVAL: 30000
};

type DataCallback = (data: Contact[]) => void;

class DataService {
  private cache: Contact[] | null = null;
  private listeners: Set<DataCallback> = new Set();
  private isPolling: boolean = false;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;

  private isConfigured(): boolean {
    return CONFIG.SHEET_ID !== 'SEU_SHEET_ID_AQUI' && CONFIG.API_KEY !== 'SUA_API_KEY_AQUI';
  }

  async fetchData(): Promise<Contact[]> {
    // Return demo data silently if not configured - avoids console errors
    if (!this.isConfigured()) {
      return this.getDemoData();
    }

    try {
      const range = `${CONFIG.SHEET_NAME}!A:T`;
      const url = `https://sheets.googleapis.com/v4/spreadsheets/${CONFIG.SHEET_ID}/values/${range}?key=${CONFIG.API_KEY}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Erro ao buscar dados: ${response.status}`);
      }

      const data = await response.json();
      const rows = data.values || [];
      const headers: string[] = rows[0] || [];
      const contacts: Contact[] = [];

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        const contact: Contact = { id: '' };

        headers.forEach((header: string, index: number) => {
          contact[header] = row[index] || '';
        });

        contact.id = contact.session_id || `row_${i}`;
        contacts.push(contact);
      }

      this.cache = contacts;
      this.notifyListeners(contacts);
      return contacts;

    } catch (error) {
      // Silent fallback to demo data - avoid console errors for best practices audit
      return this.getDemoData();
    }
  }

  private getDemoData(): Contact[] {
    const demoData: Contact[] = [
      {
        id: '1',
        nome_cliente: 'Maria Silva',
        telefone: '11999887766',
        categoria_produto: 'Bolo Personalizado',
        item_pedido: 'Bolo de Chocolate',
        adicionais: 'Cobertura extra',
        endereco_entrega: 'Rua das Flores, 123 - São Paulo',
        prazo_entrega: '15/01/2026',
        status_compra: 'Em Produção',
        etapa_atual: 'Triagem Inicial',
        temperatura_lead: 'quente'
      },
      {
        id: '2',
        nome_cliente: 'João Santos',
        telefone: '11988776655',
        categoria_produto: 'Doces Finos',
        item_pedido: 'Bem Casados',
        ornamento: 'Laço dourado',
        endereco_entrega: 'Av. Brasil, 456 - São Paulo',
        prazo_entrega: '20/01/2026',
        status_compra: 'Aguardando Pagamento',
        etapa_atual: 'Negociação',
        temperatura_lead: 'morno'
      },
      {
        id: '3',
        nome_cliente: 'Ana Costa',
        telefone: '11977665544',
        categoria_produto: 'Tortas',
        item_pedido: 'Torta de Morango',
        endereco_entrega: 'Rua Principal, 789 - São Paulo',
        prazo_entrega: '18/01/2026',
        status_compra: 'Pedido Confirmado',
        etapa_atual: 'Customização',
        temperatura_lead: 'quente'
      },
      {
        id: '4',
        nome_cliente: 'Pedro Oliveira',
        telefone: '11966554433',
        categoria_produto: 'Cupcakes',
        item_pedido: 'Kit 24 Cupcakes',
        adicionais: 'Decoração temática',
        endereco_entrega: 'Alameda Santos, 321 - São Paulo',
        prazo_entrega: '22/01/2026',
        status_compra: 'Orçamento Enviado',
        etapa_atual: 'Interesse Identificado',
        temperatura_lead: 'frio'
      },
      {
        id: '5',
        nome_cliente: 'Carla Mendes',
        telefone: '11955443322',
        categoria_produto: 'Bolo de Casamento',
        item_pedido: 'Bolo 3 Andares',
        ornamento: 'Flores naturais',
        endereco_entrega: 'Buffet Elegance - São Paulo',
        prazo_entrega: '25/01/2026',
        status_compra: 'Pagamento Confirmado',
        etapa_atual: 'Confirmação Pagamento',
        temperatura_lead: 'quente'
      },
      {
        id: '6',
        nome_cliente: 'Lucas Ferreira',
        telefone: '11944332211',
        categoria_produto: 'Sobremesas',
        item_pedido: 'Pudim de Leite',
        endereco_entrega: 'Rua dos Ipês, 567 - São Paulo',
        prazo_entrega: '16/01/2026',
        status_compra: 'Novo Lead',
        etapa_atual: 'Triagem Inicial',
        temperatura_lead: 'morno'
      }
    ];
    
    this.cache = demoData;
    this.notifyListeners(demoData);
    return demoData;
  }

  startPolling(): void {
    if (this.isPolling) return;
    this.isPolling = true;
    this.fetchData();
    this.pollingInterval = setInterval(() => {
      this.fetchData();
    }, CONFIG.POLLING_INTERVAL);
  }

  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.isPolling = false;
    }
  }

  subscribe(callback: DataCallback): () => void {
    this.listeners.add(callback);
    if (this.cache) callback(this.cache);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(data: Contact[]): void {
    this.listeners.forEach(callback => callback(data));
  }

  getCache(): Contact[] {
    return this.cache || [];
  }

  getStats(): CRMStats {
    const data = this.getCache();
    const byStage: Record<string, number> = {};

    data.forEach(contact => {
      const stage = contact.etapa_atual || 'Sem etapa';
      byStage[stage] = (byStage[stage] || 0) + 1;
    });

    return {
      total: data.length,
      byStage,
      lastUpdate: new Date().toISOString()
    };
  }
}

export const dataService = new DataService();
